import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BaseApiTemplateService } from '../shared/base-api-template-service.model';
import { Core2Config } from '../shared/core2Config';
import { ApiVisa } from '../shared/api-visa.model';
import { Observable } from 'rxjs';
import { Pagination, PagingResponseModel } from '../shared';
import { AppletImageHdrContainer } from '../../../../../../lib/models/aggregates/applet/applet-image-hdr-container.model';
import { AppletImageUpdateDto } from '../../data-transfer-objects/applet-image-update-sequence.model';

@Injectable({
    providedIn: 'root'
  })
  export class AppletImageService extends BaseApiTemplateService<AppletImageHdrContainer> {

    constructor(http: HttpClient) {
      super();
      this.apiUrl = Core2Config.DOMAIN_URL + Core2Config.PLATFORM_DOMAIN_URL_PREFIX + 'applet-images';
      this.endpoint_path = Core2Config.PLATFORM_DOMAIN_URL_PREFIX + 'applet-images';
      this.httpClient = http;
    }

    private setUrl(endpoint: string, apiVisa: ApiVisa) {
        if (apiVisa.api_domain_url){
            this.apiUrl = apiVisa.api_domain_url + Core2Config.PLATFORM_DOMAIN_URL_PREFIX + `applet-images/${endpoint}`;
            this.endpoint_path = Core2Config.PLATFORM_DOMAIN_URL_PREFIX + `applet-images/${endpoint}`
        }
    }

    // sysadmin-ep
    addImageFileAsSysadmin(appletGuid: string, files: File[], imageType: string = 'PUBLIC_READ_ONLY', apiVisa: ApiVisa): Observable<PagingResponseModel<AppletImageHdrContainer>> {
        this.setUrl('add-images/sysadmin-ep/', apiVisa);
        const url = this.apiUrl + appletGuid;
        const formData: FormData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('image_type', imageType);

        return this.httpClient.put<PagingResponseModel<AppletImageHdrContainer>>(url, formData, this.getHttpHeader(apiVisa))
    }

    replaceImageFileAsSysadmin(imageGuid: string, file: File, apiVisa: ApiVisa): Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('replace/sysadmin-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        const formData: FormData = new FormData();
        if (file) {
          formData.append('file', file);
        }
    
        return this.httpClient.put<PagingResponseModel<AppletImageHdrContainer>>(url, formData, this.getHttpHeader(apiVisa))
    }

    getImageFileAsSysadmin(imageGuid: string, apiVisa: ApiVisa): Observable<Blob> {
        this.setUrl('image-file/sysadmin-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.get<Blob>(url, this.getHttpHeaderBlob(apiVisa));
    }

    deleteImageFileAsSysadmin(imageGuid: string, apiVisa: ApiVisa): Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('image-file/sysadmin-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.delete<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }

    getByCriteriaAsSysadmin(pagination: Pagination, apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('sysadmin-ep/query?', apiVisa);
        const url = this.apiUrl + pagination.getQueryKeyValues();
        return this.httpClient.get<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }

    updateImageSequenceAsSysadmin(dto: AppletImageUpdateDto[], apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('update-image-sequence/sysadmin-ep', apiVisa);
        const url = this.apiUrl;
        return this.httpClient.post<PagingResponseModel<AppletImageHdrContainer>>(url, dto, this.getHttpHeader(apiVisa));
    }


    //public-ep
    getImageFileAsPublic(imageGuid: string, apiVisa: ApiVisa): Observable<Blob> {
        this.setUrl('image-file/public-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.get<Blob>(url, this.getHttpHeaderBlob(apiVisa));
    }

    getByCriteriaAsPublic(pagination: Pagination, apiVisa: ApiVisa): Observable<PagingResponseModel<AppletImageHdrContainer>> {
        this.setUrl('public-ep/query?', apiVisa);
        const url = this.apiUrl + pagination.getQueryKeyValues();
        return this.httpClient.get<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }

    // developer-ep
    addImageFileAsDeveloper(appletGuid: string, files: File[], imageType: string = 'PUBLIC_READ_ONLY', apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('add-images/developer-ep/', apiVisa);
        const url = this.apiUrl + appletGuid;
        const formData: FormData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('image_type', imageType);

        return this.httpClient.put<PagingResponseModel<AppletImageHdrContainer>>(url, formData, this.getHttpHeader(apiVisa))
    }

    replaceImageFileAsDeveloper(imageGuid: string, file: File, apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('replace/developer-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        const formData: FormData = new FormData();
        if (file) {
          formData.append('file', file);
        }
    
        return this.httpClient.put<PagingResponseModel<AppletImageHdrContainer>>(url, formData, this.getHttpHeader(apiVisa))
    }

    getImageFileAsDeveloper(imageGuid: string, apiVisa: ApiVisa): Observable<Blob> {
        this.setUrl('image-file/developer-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.get<Blob>(url, this.getHttpHeaderBlob(apiVisa));
    }

    deleteImageFileAsDeveloper(imageGuid: string, apiVisa: ApiVisa): Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('image-file/developer-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.delete<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }

    getByCriteriaAsDeveloper(pagination: Pagination, apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('developer-ep/query?', apiVisa);
        const url = this.apiUrl + pagination.getQueryKeyValues();
        return this.httpClient.get<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }
    
    //login-tenant-ep
    getImageFileAsLoginTenant(imageGuid: string, apiVisa: ApiVisa): Observable<Blob> {
        this.setUrl('image-file/login-tenant-ep/', apiVisa);
        const url = this.apiUrl + imageGuid;
        return this.httpClient.get<Blob>(url, this.getHttpHeaderBlob(apiVisa));
    }

    getByCriteriaAsLoginTenant(pagination: Pagination, apiVisa: ApiVisa) : Observable<PagingResponseModel<AppletImageHdrContainer>>{
        this.setUrl('login-tenant-ep/query?', apiVisa);
        const url = this.apiUrl + pagination.getQueryKeyValues();
        return this.httpClient.get<PagingResponseModel<AppletImageHdrContainer>>(url, this.getHttpHeader(apiVisa));
    }
}